"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { cancelOrder, fetchOrders, OPEN_STATUSES, type ProOrder } from "@/lib/orders-api";
import { useSession } from "@/lib/useSignIn";

const fmt = (v: string | null) =>
  v === null ? "n/a" : Number(v).toLocaleString("en-NG", { maximumFractionDigits: 2 });

const STATUS_TONE: Record<string, string> = {
  filled: "text-buy",
  cancelled: "text-fg-muted",
  rejected: "text-sell",
  failed: "text-sell",
};

const Row = ({ o, onCancel, cancelling }: { o: ProOrder; onCancel: (id: string) => void; cancelling: boolean }) => {
  const open = OPEN_STATUSES.includes(o.status);
  return (
    <div className="flex items-center justify-between gap-2 border-b border-line/50 px-2 py-1.5 text-[11px]">
      <div className="flex flex-col">
        <span className={o.side === "buy" ? "text-buy" : "text-sell"}>
          {o.side.toUpperCase()} {fmt(o.quantity)} USD {o.type === "limit" ? `@ ${fmt(o.price)}` : "(market)"}
        </span>
        <span className="text-fg-muted">
          filled {fmt(o.filled_quantity)} / {fmt(o.quantity)} ·{" "}
          <span className={STATUS_TONE[o.status] ?? "text-fg-secondary"}>{o.status.replace("_", " ")}</span>
        </span>
      </div>
      {open && (
        <button
          type="button"
          disabled={cancelling}
          onClick={() => onCancel(o.id)}
          className="rounded border border-line px-2 py-1 text-[10px] text-fg-muted transition-colors hover:border-sell hover:text-sell disabled:opacity-40"
        >
          Cancel
        </button>
      )}
    </div>
  );
};

const OrdersPanel = () => {
  const qc = useQueryClient();
  const { isSignedIn } = useSession();

  const { data, isLoading } = useQuery({
    queryKey: ["orders"],
    queryFn: fetchOrders,
    enabled: isSignedIn,
    // Fill settlement runs on a ~15s server sync; polling faster only re-reads our own DB.
    refetchInterval: 10_000,
  });

  const cancel = useMutation({
    mutationFn: cancelOrder,
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["orders"] });
      void qc.invalidateQueries({ queryKey: ["balances"] });
    },
  });

  if (!isSignedIn) return <div className="p-3 text-xs text-fg-muted">Sign in to see your orders.</div>;
  if (isLoading) return <div className="p-3 text-xs text-fg-muted">Loading orders…</div>;

  const orders = data?.orders ?? [];
  if (orders.length === 0) return <div className="p-3 text-xs text-fg-muted">No orders yet.</div>;

  return (
    <div className="flex h-full flex-col overflow-y-auto">
      {cancel.isError && (
        <p className="px-2 pt-2 text-[11px] text-sell">{(cancel.error as Error).message}</p>
      )}
      {orders.map((o) => (
        <Row key={o.id} o={o} onCancel={(id) => cancel.mutate(id)} cancelling={cancel.isPending} />
      ))}
    </div>
  );
};

export default OrdersPanel;
